// Hosted solution

import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import sha256 from "crypto-js/sha256";
import PuffLoader from "react-spinners/PuffLoader";
import "./gateway.scss";
import "react-toastify/dist/ReactToastify.css";
import { ReactComponent as UserSvg } from "../../assets/icons/user.svg";
import { ReactComponent as CardSvg } from "../../assets/icons/card.svg";
import { ReactComponent as CalendarSvg } from "../../assets/icons/calendar.svg";

const publicUrlSandbox = "https://sandbox.paydeca.com";
const publicUrlProduction = "https://api.paydeca.com";
const publicUrlDev = "https://dev.veripaygate.com/api";

const endpoint = {
  prod: {
    purchase: `${publicUrlProduction}/fn-execute/payment/purchase`,
    validation: `${publicUrlProduction}/fn-execute/provision/validation`,
    reverseValidation: `${publicUrlProduction}/fn-execute/reverse/validation`,
    reversePayout: `${publicUrlProduction}/fn-execute/reverse/payout`,
  },
  dev: {
    purchase: `${publicUrlDev}/fn-execute/payment/purchase`,
    validation: `${publicUrlDev}/fn-execute/provision/validation`,
    reverseValidation: `${publicUrlDev}/fn-execute/reverse/validation`,
    reversePayout: `${publicUrlDev}/fn-execute/reverse/payout`,
  },
  sandbox: {
    purchase: `${publicUrlSandbox}/fn-execute/payment/purchase`,
    validation: `${publicUrlSandbox}/fn-execute/provision/validation`,
    reverseValidation: `${publicUrlSandbox}/fn-execute/reverse/validation`,
    reversePayout: `${publicUrlSandbox}/fn-execute/reverse/payout`,
  },
};

const VALIDATION_TIMEOUT_MS = 20000;

// A redirect target comes from a server response, so only a plain web address is followed -
// never a javascript: or data: URL.
const isSafeRedirect = (value) => {
  try {
    const { protocol } = new URL(value);
    return protocol === "https:" || protocol === "http:";
  } catch {
    return false;
  }
};

const DEVICE_ID_KEY = "paydeca_device_id";

const newDeviceId = () => {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch (error) {
    // fall through to the random fallback
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
};

const getDeviceId = () => {
  try {
    const stored = window.localStorage.getItem(DEVICE_ID_KEY);
    if (stored) return stored;

    const created = newDeviceId();
    window.localStorage.setItem(DEVICE_ID_KEY, created);
    return created;
  } catch (error) {
    // Storage is unavailable, so this payment gets an id of its own.
    return newDeviceId();
  }
};

const companyInfoMap = {
  "onefunforall.net": {
    name: "Artzub Limited",
    address: "Harju maakond, Tallinn, Põhja-Tallinna linnaosa, Toompuiestee 33a, Estonia, 10149",
  },
  "skills-trade.com": {
    name: "RENATASTRADAS",
    address: "Šiauliai, Vytauto g. 147-18, LT-76341",
  },
  "startmoto.co.uk": {
    name: "Any Place any Time",
    address: "Unity St, St Philip S, Bristol, United Kingdom, BS2 0HN",
  },
};

const getCompanyInfo = () => {
  try {
    const referrer = document.referrer;
    if (!referrer) return null;
    const hostname = new URL(referrer).hostname.replace(/^www\./, "");
    return companyInfoMap[hostname] || null;
  } catch {
    return null;
  }
};

const month = ["Month", "01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];
const year = [
  "Year",
  "2026",
  "2027",
  "2028",
  "2029",
  "2030",
  "2031",
  "2032",
  "2033",
  "2034",
  "2035",
  "2036",
];


const Gateway = () => {
  const { refno, env } = useParams();
  const [searchParams] = useSearchParams();
  const isReverseFlow = searchParams.get("flow") === "reverse";
  // Secret from the link of a reverse withdrawal: it proves this page was handed the payout
  // rather than guessed at from its reference number.
  const hostedToken = searchParams.get("t") || "";
  
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showError, setShowError] = useState(false);
  const [provData, setProvData] = useState();
  const [payoutDone, setPayoutDone] = useState(false);

  // A reverse withdrawal is the same page the other way round: the card typed in is the one the
  // money is paid out to, so there is no CVV and nothing is charged.
  const isPayout = provData?.type === "withdrawal";
  const [companyInfo] = useState(() => getCompanyInfo());

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm();

  useEffect(() => {
    validateProvision();
  }, []);

  // A validation that never answers would leave the spinner up for as long as the server holds
  // the connection, so it is cut off and shown as an error instead.
  const fetchValidation = async (url) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), VALIDATION_TIMEOUT_MS);
    try {
      const query = hostedToken ? `?t=${encodeURIComponent(hostedToken)}` : "";
      const response = await fetch(`${url}/${encodeURIComponent(refno)}${query}`, {
        signal: controller.signal,
      });
      const result = await response.json();
      return { response, result };
    } finally {
      clearTimeout(timer);
    }
  };

  const validateProvision = async () => {
    try {
      const endpoints = endpoint[env || "sandbox"];
      const validationUrl = isReverseFlow ? endpoints.reverseValidation : endpoints.validation;
      let { response, result } = await fetchValidation(validationUrl);

      // The link of a reverse payment carries flow=reverse, but a link that lost it on the way
      // (a redirect, a copied address) must still open. A reference number that no provision
      // knows is looked up as a reverse payment before the page gives up on it.
      if (!isReverseFlow && response.status === 400) {
        const reverse = await fetchValidation(endpoints.reverseValidation).catch(() => null);
        if (reverse?.response.ok) ({ response, result } = reverse);
      }

      if(response.status === 400 && result.message){
        toast.error(result.message);
        setLoading(false);
        setShowError(true);
        return;
      }

      setProvData(result);
      setLoading(false);
    } catch (error) {
      toast.error("Error! Please try again.");
      setLoading(false);
      setShowError(true);
    }
  };

  const submitPayout = async (data) => {
    setIsSubmitting(true);

    const creditCard = {
      holder: data.creditCard.holder.trim(),
      number: data.creditCard.number.replace(/\s/g, ""),
      expiryMonth: data.creditCard.expiryMonth,
      expiryYear: data.creditCard.expiryYear,
    };

    if (!isChValid(creditCard, { cvv: false })) {
      toast.error("Invalid card holder data");
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch(
        `${endpoint[env || "sandbox"].reversePayout}/${encodeURIComponent(refno)}`,
        {
          method: "POST",
          body: JSON.stringify({ creditCard, token: hostedToken }),
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );

      const result = await response.json().catch(() => ({}));

      if (response.ok) {
        if (isSafeRedirect(result.redirectUrl)) {
          window.location.href = result.redirectUrl;
          return;
        }
        setPayoutDone(true);
        return;
      }

      // The payout is over, whichever way it ended: send the user back to where they came from.
      if (isSafeRedirect(result.redirectUrl)) {
        window.location.href = result.redirectUrl;
        return;
      }

      // The payout is gone (expired or already taken) - nothing left to correct on this form.
      if (response.status === 400 && result.message === "Transaction expired") {
        toast.error(result.message);
        setShowError(true);
        return;
      }

      // A card the user can correct; the payout is still open.
      toast.error(result.message || "Error! Please try again.");
      setIsSubmitting(false);
    } catch (error) {
      toast.error("Error! Please try again.");
      setIsSubmitting(false);
    }
  };

  const onSubmit = (data) => {
    if (!data) return;

    if (isPayout) {
      submitPayout(data);
      return;
    }

    setIsSubmitting(true);

    const metaData = {
      browserUserAgent: navigator.userAgent,
      language: navigator.language,
      colorDepth: window.screen.colorDepth,
      screenHeight: window.screen.height,
      screenWidth: window.screen.width,
      browserTZ: new Date().getTimezoneOffset(),
      javaEnabled: navigator.javaEnabled(),
      acceptHeader: "/",
    };

    const creditCard = { ...data.creditCard, number: data.creditCard.number.replace(/\s/g, "") };

    const dataObj = {
      creditCard,
      referenceNo: refno,
      deviceId: getDeviceId(),
      browserInfo: metaData,
      provision: true,
    };

    dataObj.hash = hashBody(dataObj);
    startPayment(dataObj);
  };

  const hashBody = (body) => {
    return sha256(JSON.stringify(body)).toString();
  };

  const startPayment = async (data) => {
    try {
      if (!isChValid(data.creditCard)) {
        toast.error("Invalid card holder data");
        setIsSubmitting(false);
        return;
      }

      const response = await fetch(endpoint[env || "sandbox"].purchase, {
        method: "POST",
        body: JSON.stringify(data),
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      const result = await response.json();
      if (result?.hasOwnProperty("redirect_url")) {
        window.location.href = result.redirect_url;
        return;
      }
      setShowError(true);
    } catch (error) {
      setShowError(true);
      setIsSubmitting(false);
    }
  };

  const isChValid = (data, { cvv = true } = {}) => {
    const regexHolder = /^([a-zA-Z]+[ '-]?)*[a-zA-Z]+$/;
    const regexCvv = /^\d{3,4}$/;

    if (
      !regexHolder.test(data.holder) ||
      (cvv && !regexCvv.test(data.cvv)) ||
      !isCreditCardNumberValid(data.number)
    ) {
      return false;
    }

    return true;
  };

  const isCreditCardNumberValid = (cardNumber) => {
    const cardDigits = cardNumber.replace(/\s/g, "").split("").map(Number);
    cardDigits.reverse();

    let sum = 0;
    let isEven = false;

    for (let i = 0; i < cardDigits.length; i++) {
      let digit = cardDigits[i];

      if (isEven) {
        digit = digit * 2 - (digit > 4 ? 9 : 0);
      }

      sum += digit;
      isEven = !isEven;
    }

    return sum % 10 === 0;
  };

  const handleCardNumberChange = (event) => {
    const rawValue = event.target.value.replace(/\s/g, "");
    const formattedValue = rawValue
      .replace(/\D/g, "")
      .replace(/(.{4})/g, "$1 ")
      .trim();

    setValue("creditCard.number", formattedValue);
  };

  return (
    <section className="payment-section">
      {loading ? (
        <PuffLoader color="#36d7b7" />
      ) : payoutDone ? (
        <section className="payout-result">
          <div className="payout-result-card">
            <div className="payout-result-icon">
              <svg viewBox="0 0 52 52" aria-hidden="true">
                <circle className="payout-result-circle" cx="26" cy="26" r="24" fill="none" />
                <path className="payout-result-check" fill="none" d="M15 27l8 8 14-16" />
              </svg>
            </div>
            <h1>Withdrawal requested</h1>
            <p className="payout-result-subtitle">
              We have received your request and are sending the funds to your card.
            </p>

            <div className="payout-result-amount">
              <span>Amount</span>
              <strong>
                {provData?.amount} {provData?.currency}
              </strong>
            </div>

            <ul className="payout-result-steps">
              <li className="done">
                <span className="dot" />
                Request received
              </li>
              <li className="active">
                <span className="dot" />
                Processing
              </li>
              <li>
                <span className="dot" />
                Funds on their way
              </li>
            </ul>

            <dl className="payout-result-meta">
              <dt>Reference</dt>
              <dd>{refno}</dd>
            </dl>

            <p className="payout-result-note">
              It can take a while for the money to show on your card statement. You can close this
              page.
            </p>
          </div>
        </section>
      ) : showError ? (
        <section className="result-section">
          <div className="result-container">
            <div className="result-content">
              <div className="success-message">
                <div className="failure">
                  <div className="failure-icon">
                    <i>×</i>
                  </div>
                  <h1>Payment failure</h1>
                  <p>Please try again later</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : (
        provData && (
          <div className="payment-form">
            <div className="form-container">
              <div className="form-content">
                <h3>{isPayout ? "Withdrawal Details" : "Payment Details"}</h3>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="form-group">
                    <label>
                      <CardSvg /> Card Number
                    </label>
                    <input
                      type="tel"
                      minLength="16"
                      maxLength="23"
                      value={watch("creditCard.number")}
                      {...register("creditCard.number", {
                        required: true,
                        minLength: 16,
                        maxLength: 23,
                        onChange: handleCardNumberChange,
                      })}
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      <UserSvg /> Card Holder
                    </label>
                    <input
                      type="text"
                      maxLength="100"
                      {...register("creditCard.holder", {
                        required: true,
                      })}
                    />
                  </div>
                  <div className="row">
                    <div className="form-group">
                      <label>
                        <CalendarSvg /> Exp. Date
                      </label>
                      <div className="row">
                        <select
                          {...register("creditCard.expiryMonth", {
                            required: true,
                            validate: (value) => value !== "Month",
                          })}
                        >
                          {month.map((m) => (
                            <option key={m} value={m}>
                              {m}
                            </option>
                          ))}
                        </select>

                        <select
                          {...register("creditCard.expiryYear", {
                            required: true,
                            validate: (value) => value !== "Year",
                          })}
                        >
                          {year.map((y) => (
                            <option key={y} value={y}>
                              {y}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {!isPayout && (
                      <div className="form-group">
                        <label>
                          <CardSvg /> CVV
                        </label>
                        <div className="cvv">
                          <input
                            type="tel"
                            className="card-input"
                            maxLength="4"
                            minLength="3"
                            {...register("creditCard.cvv", {
                              required: true,
                              maxLength: 4,
                              minLength: 3,
                            })}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="pay-button"
                    disabled={Object.keys(errors).length > 0 || isSubmitting}
                  >
                    {isSubmitting ? (
                      <PuffLoader color="#fff" size={20} />
                    ) : (
                      `${isPayout ? "Withdraw" : "Pay"} ${provData?.amount} ${provData?.currency}`
                    )}
                  </button>
                  {companyInfo && (
                    <div className="company-info">
                      <p className="company-name">{companyInfo.name}</p>
                      <p className="company-address">{companyInfo.address}</p>
                    </div>
                  )}
                </form>
              </div>
            </div>
            <div></div>
          </div>
        )
      )}
    </section>
  );
};

export default Gateway;
